/**
 * ToolUseGroup
 *
 * Collapses a run of identical consecutive tool calls (e.g. 4× "Web Research")
 * into a single summary row to keep the reasoning timeline tidy. Expanding the
 * row reveals each individual call as a normal ToolUseItem with its own
 * input/result.
 */
import { AlertCircle, CheckCircle } from '@untitledui/icons';
import type React from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';

import { getToolConfig, type ToolResult } from './design-tokens';
import { TimelineConnectorLine } from './TimelineConnectorLine';
import { getToolIcon } from './toolIcons';
import { isToolRunning } from './toolRunning';
import { RunningIndicator } from './toolStatus';
import { ToolUseItem } from './ToolUseItem';

interface ToolUseGroupProps {
  /** Two or more consecutive tool results that share the same tool_name. */
  toolResults: ToolResult[];
  isFirst?: boolean;
  isLast?: boolean;
}

export const ToolUseGroup: React.FC<ToolUseGroupProps> = ({
  toolResults,
  isFirst = false,
  isLast = false,
}) => {
  const config = getToolConfig(toolResults[0].tool_name);
  const count = toolResults.length;
  // Count a tool as failed when it carries an explicit 'error' status OR an error
  // string — a status of 'error' without a message would otherwise be missed and
  // render as a success check.
  const errorCount = toolResults.filter(
    r => r.status === 'error' || !!r.error
  ).length;
  const hasError = errorCount > 0;
  const runningCount = toolResults.filter(isToolRunning).length;
  const anyRunning = runningCount > 0;
  const padding = isFirst ? 'pt-3 pb-1' : 'py-1';

  // Status indicator: pulse while any tool in the group is still running, then
  // collapse to the normal check/alert. Missing status (legacy) is never running.
  let statusIndicator: React.ReactNode;
  if (anyRunning) {
    statusIndicator = <RunningIndicator />;
  } else if (hasError) {
    statusIndicator = (
      <AlertCircle
        className='w-3.5 h-3.5 text-utility-error-500'
        strokeWidth={2}
      />
    );
  } else {
    statusIndicator = (
      <CheckCircle
        className='w-3.5 h-3.5 text-utility-success-500'
        strokeWidth={2}
      />
    );
  }

  // Subtitle: only show when it adds information. The "{count}×" chip already
  // conveys the call count, so a plain "N calls" line is redundant — omit it
  // and only surface progress (while running) or failures.
  let summaryText = '';
  if (anyRunning) {
    summaryText = `${count - runningCount}/${count} done · ${runningCount} running`;
  } else if (hasError) {
    summaryText = `${count} done · ${errorCount} failed`;
  }

  return (
    <div className='relative'>
      {/* Connector line to the next sibling row */}
      {!isLast && (
        <TimelineConnectorLine className='left-[8px] top-[28px] bottom-[-4px]' />
      )}

      <AccordionItem className={padding}>
        <AccordionTrigger className='w-full flex items-start gap-2 text-left hover:bg-bg-secondary rounded-md px-1 py-0.5 -mx-1'>
          {/* Icon - pulses while any tool in the group is still running */}
          <div
            className={`size-[18px] flex items-center justify-center shrink-0 mt-0.5 ${
              anyRunning ? 'animate-pulse' : ''
            }`}
            data-icon-type={config.icon}
          >
            {getToolIcon(config.icon, 'w-4 h-4 text-fg-primary')}
          </div>

          {/* Content */}
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-normal text-fg-secondary'>
                {config.displayName}
              </span>
              <span className='text-xs font-medium text-fg-tertiary'>
                {count}×
              </span>
              {statusIndicator}
              <AccordionChevron
                className='w-4 h-4 text-tertiary ml-auto'
                strokeWidth={1.5}
              />
            </div>
            {/* Subtitle — only when it adds info (progress / failures) */}
            {summaryText && (
              <p className='text-xs text-fg-tertiary truncate'>{summaryText}</p>
            )}
          </div>
        </AccordionTrigger>

        <AccordionPanel className='ml-[26px] mt-1'>
          <div className='space-y-0'>
            {toolResults.map((toolResult, index, arr) => (
              <ToolUseItem
                // eslint-disable-next-line react/no-array-index-key -- consecutive identical calls have no stable id
                key={`${toolResult.tool_name}-${index}`}
                toolResult={toolResult}
                isFirst={index === 0}
                isLast={index === arr.length - 1}
              />
            ))}
          </div>
        </AccordionPanel>
      </AccordionItem>
    </div>
  );
};

export default ToolUseGroup;
